import {
  BrowserClient,
  defaultIntegrations,
  Hub,
  defaultStackParser,
  makeFetchTransport
} from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { CaptureContext } from '@sentry/types';

function getTraceRates() {
  if (CONFIG.deployTarget === 'production') {
    return {
      tracesSampleRate: 0.6,
      sampleRate: 1.0
    };
  }
  if (CONFIG.isDevelopment) {
    return {
      sampleRate: 0.0,
      tracesSampleRate: 0.0
    };
  }
  return {
    sampleRate: 1.0,
    tracesSampleRate: 1.0
  };
}

let sentry: Hub | undefined;

export function initSentry(): void {
  if (sentry) return;
  const client = new BrowserClient({
    dsn: CONFIG.sentryDsn,
    integrations: [...defaultIntegrations, new BrowserTracing()],
    autoSessionTracking: true,
    sendClientReports: true,
    environment: CONFIG.deployTarget,
    stackParser: defaultStackParser,
    transport: makeFetchTransport,
    ...getTraceRates()
  });
  const sentryHub = new Hub(client);
  sentryHub.startSession({ ignoreDuration: true });
  sentryHub.captureSession();
  sentryHub.configureScope((scope) => {
    scope.addEventProcessor((event) => {
      if (sentryHub) {
        if (!window.navigator && !window.location && !window.document) {
          return event;
        }

        const url =
          (event.request && event.request.url) ||
          (window.location && window.location.href);

        const { referrer } = window.document;
        const { userAgent } = window.navigator;

        const headers = {
          ...(event.request && event.request.headers),
          ...(referrer && { Referer: referrer }),
          ...(userAgent && { 'User-Agent': userAgent })
        };
        const request = { ...(url && { url }), headers };

        return { ...event, request };
      }
      return event;
    });
  });
  sentry = sentryHub;
}

export const configureSentryScope: Hub['configureScope'] = (callback) => {
  if (!sentry) {
    console.warn('Trying to report error before sentry initialized!');
    return;
  }

  sentry.configureScope(callback);
};

export const reportSentryError = (
  exception: Parameters<Hub['captureException']>[0],
  captureContext?: CaptureContext
): string => {
  if (!sentry) {
    console.warn('Trying to report error before sentry initialized!');
    return '';
  }
  return sentry.captureException(exception, { captureContext });
};
